import { PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { JsxFragment } from "../JsxFragment";
import { Control, ControlPropertyValues, IControl } from "./Control";
import { ControlRef } from "./ControlRef";
import { NAMESPACE } from "./_Namespace";

import "./Label.scss";

export type LabelPropertyValues =
    ControlPropertyValues
    & {
        associatedControlId: string;
        associatedControlRef: ControlRef;
        colon: boolean;
    };

export type LabelProperties = {
    [name in keyof LabelPropertyValues]: Property<LabelPropertyValues[name]>
};

export interface ILabel extends IControl, LabelProperties
{
}

export class Label extends Control implements ILabel
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Label";
    public static override className = "Label";

    // #region Global Variables

    public static labelableElementTypes = ["button", "input", "keygen", "meter", "output", "progress", "select", "textarea"];

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<LabelPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                colon: true,
                text: "Label",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public associatedControlId!: PropertyString;
    public associatedControlRef!: Property<ControlRef>;
    public colon!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<LabelPropertyValues>)
    {
        super(propertyValues);

        const
            _associatedControlId = this.associatedControlId.get(),
            _associatedControl = this.associatedControlRef.get();

        this.addCssClass("rad-control-label field");

        if (_associatedControl?.current)
        {
            if (_associatedControl.current.labelTargetId)
                this.$$element.get()?.attr("for", _associatedControl.current.labelTargetId);
        }
        else if (_associatedControlId)
        {
            const
                $$associatedControl = $$("#" + _associatedControlId);

            if ($$associatedControl.length)
            {
                setTimeout(() =>
                {
                    this.associatedControlRef.set($$associatedControl.radControl());
                });
            }
        }

        // Trigger Property Changes
        this.colon.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _associatedControlRefChanged(change: PropertyChange<ControlRef>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        if (change.currentValue?.current?.labelTargetId)
            $$element.attr("for", change.currentValue?.current?.labelTargetId);
        else
            $$element.removeAttr("for");
    }
    protected _associatedControlIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _colonChanged(change: PropertyChange<boolean>)
    {
        this._updateLayout();
    }
    protected _updateLayout()
    {
        const
            $$element = this.$$element.get(),
            text = this.text.get() ?? StringHelper.empty;

        if (text)
        {
            $$element?.attr("title", text);
            $$element?.find(">span").first().text(text + (this.colon.get() ? ":" : StringHelper.empty));
        }
        else
        {
            $$element?.removeAttr("title");
            $$element?.find(">span").first().text(null);
        }
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: LabelPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.associatedControlId = new PropertyString({
            context: this,
            initialValue: propertyValues.associatedControlId,
            changeHandler: this._associatedControlIdChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Behavior" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Associated Control Id" }),
            ],
        });
        this.associatedControlRef = new Property<ControlRef>({
            context: this,
            dotNetType: "System.Object, System.Private.CoreLib",
            initialValue: propertyValues.associatedControlRef,
            changeHandler: this._associatedControlRefChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Behavior" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Associated Control" }),
            ],
        });
        this.colon = new PropertyBoolean({
            context: this,
            initialValue: propertyValues.colon,
            changeHandler: this._colonChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Colon" }),
            ],
        });
    }

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <label>
                <span></span>
                &nbsp;
                <span class="rad-asterisk" aria-label="Required Field.">*</span>
            </label>
        );
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);
        this._updateLayout();
    }

    // #endregion

    // #region Event Handlers

    protected onClick(evt: IJNativeEvent)
    {
        /* Check the type of the associated control's element to see if it's labelable in the spec:
            see https://www.w3.org/TR/html5/forms.html#category-label */

        const
            self = evt.data.self as this;

        if (!self.associatedControlRef.get())
            return;

        const
            labelTarget = $$("#" + self.associatedControlRef.get()?.current?.labelTargetId);

        if (!labelTarget.length)
            return;

        const
            nodeName = labelTarget.prop("nodeName");

        if (!nodeName)
            return;

        const
            labelTargetType = nodeName.toLowerCase();

        // On NON labelable element, trigger a focus. edge case = element type is an input with type=hidden, or switches
        if (labelTarget.hasClass("rad-control-switch"))
        {
            labelTarget.find(">input").trigger("focus");
            evt.preventDefault();
        }
        else if (labelTargetType === "input" && labelTarget.attr("type") === "hidden" || Label.labelableElementTypes.indexOf(labelTargetType) === -1)
        {
            labelTarget.trigger("focus");
            evt.preventDefault();
        }
    }

    // #endregion

    // #endregion
}